import { faEnvelope, faLock, faUser } from "@fortawesome/pro-light-svg-icons"
import {
  CalloutBox,
  createErrorFromSignupResponse,
  FlexBox,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"

import { useCoachAccountCreation } from "./useCoachAccountCreation"
import useCoachOnboardingStore from "./useCoachOnboardingStore"
import Primary3DButton from "../../components/buttons/Primary3DButton"
import OnboardingPageContentWrapper from "../../components/onboarding/onboardingPageContentWrapper/OnboardingPageContentWrapper"
import OnboardingPageWrapper from "../../components/onboarding/onboardingPageWrapper/OnboardingPageWrapper"
import TextInputRect from "../../components/textInputRect/TextInputRect"
import { CoachPaywallChoice } from "../../constants"
import useMobile from "../../hooks/useMobile"
import { signup } from "../../networking/authentication"
import { useCoachOnboardIntakeFormQuestionnaireQuery } from "../../networking/queries"
import {
  AlertInterface,
  QuestionAnswerMappingInterface,
  QuestionInterface,
  QuestionType,
  QuestionnairesInterface,
} from "../../types"
import {
  ANALYTIC_EVENTS,
  GAEvent,
  trackAnalyticEvent,
  trackGAEvent,
} from "../../util/analytics"
import { useRouter } from "../../util/router"
import { localStorageItems } from "../../util/storage"
import { useCoachingStore } from "../coaching/useCoachingStore"

// Build a default answer mapping by picking the first answer of every
// single/multi-choice question, and a neutral placeholder for freeform.
// We auto-fill rather than skip submission so the backend's coach-matching
// algorithm still has signal — albeit biased toward "first answer".
const buildDefaultAnswerMapping = (
  questionnaire: QuestionnairesInterface | null | undefined
): QuestionAnswerMappingInterface => {
  if (!questionnaire) return {}
  const mapping: QuestionAnswerMappingInterface = {}
  questionnaire.questions.forEach((q: QuestionInterface) => {
    if (q.type === QuestionType.FREEFORM) {
      mapping[q.id] = "Skipped"
    } else if (q.answers.length > 0) {
      mapping[q.id] = [q.answers[0].id]
    }
  })
  return mapping
}

const CoachSignUpSkip = () => {
  const isMobile = useMobile()
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(true)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [firstName, setFirstName] = useState("")
  const [formAlert, setFormAlert] = useState<AlertInterface | null>(null)

  const { setSourcePage } = useCoachingStore()
  const {
    isSubmittingData,
    parentData,
    setParentData,
    setQuestionAnswerMapping,
  } = useCoachOnboardingStore()
  const { data: questionnaire, isLoading: isQuestionnaireLoading } =
    useCoachOnboardIntakeFormQuestionnaireQuery()
  const { uploadOnboardingSecondaryData } = useCoachAccountCreation()

  // Auto-fill the parent intake questionnaire with first-answer defaults
  // as soon as the questionnaire query resolves. Putting this in an effect
  // (rather than inside handleSignUp) means the values are in the store
  // before the upload closure is created, sidestepping the stale-closure
  // pattern that bit the existing CoachSignUp flow. Child defaults are
  // handled by uploadOnboardingSecondaryData (it inserts a "Child"
  // placeholder when childrenData is empty).
  useEffect(() => {
    if (!questionnaire) return
    setQuestionAnswerMapping(buildDefaultAnswerMapping(questionnaire))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire])

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      if (!firstName || !email || !password) {
        setFormAlert({ type: "error", message: "Please fill out all fields" })
        return
      }
      if (!questionnaire) {
        setFormAlert({
          type: "error",
          message: "Still loading — please try again in a moment.",
        })
        return
      }

      await signup(email, password)
      await uploadOnboardingSecondaryData()
      localStorage.setItem(localStorageItems.isFromCoachOnboarding, "true")
      localStorage.setItem(
        localStorageItems.planChoiceFromPaywall,
        CoachPaywallChoice.WITH_COACH
      )
      setSourcePage("coach-onboard")
      trackAnalyticEvent(ANALYTIC_EVENTS.COACH_ONBOARDING_SIGNUP, {
        skipped_quiz: true,
      })
      trackGAEvent(GAEvent.CREATE_ACCOUNT, undefined, { email, firstName })
      router.push("/coaching")
    } catch (error) {
      const errorMessage = createErrorFromSignupResponse(error)
      if (errorMessage) setFormAlert({ type: "error", message: errorMessage })
    }
  }

  return (
    <OnboardingPageWrapper
      style={
        isMobile
          ? {
              backgroundImage: "url(/images/background/cloud-bg.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : { background: "transparent" }
      }
    >
      <OnboardingPageContentWrapper
        backgroundImageOnDesktop="url(/images/background/cloud-bg.png)"
        paddingTop="5dvh"
      >
        <img
          src="/images/logos/logo_white.svg"
          alt="Joon"
          style={{
            width: "110px",
            zIndex: 1,
            marginBottom: SPACING.space3,
            userSelect: "none",
            pointerEvents: "none",
            opacity: isLoading ? 0 : 1,
            transition: "opacity 0.1s ease-in-out",
          }}
          onLoad={() => setIsLoading(false)}
        />
        <Typography
          variant="h2Serif"
          textAlign="center"
          style={{ maxWidth: 320 }}
        >
          See your top coaches
        </Typography>
        <Typography
          variant="body"
          textAlign="center"
          color={JoonUIColor.text.secondary}
          style={{ maxWidth: 320, marginTop: SPACING.space2 }}
        >
          Create your account and we&apos;ll take you straight to our
          recommended coaches.
        </Typography>

        <form
          onSubmit={handleSignUp}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            gap: SPACING.space8,
            width: "100%",
          }}
        >
          <FlexBox
            direction="column"
            wrap={false}
            gap={SPACING.space6}
            style={{
              padding: SPACING.space6,
              background: "rgba(255, 255, 255, 0.6)",
              borderRadius: SPACING.space2,
            }}
          >
            <FlexBox direction="column" gap={SPACING.space1}>
              <Typography variant="body">First name</Typography>
              <TextInputRect
                type="text"
                icon={faUser}
                name="first_name"
                placeholder="First name"
                fullWidth={true}
                onChange={(e: any) => {
                  // Mirror the CoachSignUp pattern: write the name to the
                  // store on every keystroke so the latest render's upload
                  // closure carries it. The handler-time setParentData is
                  // racy against the upload's stale closure.
                  setFirstName(e.target.value)
                  setParentData({ ...parentData, name: e.target.value })
                }}
                value={firstName}
              />
            </FlexBox>
            <FlexBox direction="column" gap={SPACING.space1}>
              <Typography variant="body">Email</Typography>
              <TextInputRect
                icon={faEnvelope}
                type="email"
                placeholder="Email"
                name="email"
                fullWidth={true}
                onChange={(e: any) => setEmail(e.target.value)}
                value={email}
              />
            </FlexBox>
            <FlexBox direction="column" gap={SPACING.space1}>
              <Typography variant="body">Password</Typography>
              <TextInputRect
                icon={faLock}
                type="password"
                placeholder="Password"
                name="password"
                fullWidth={true}
                onChange={(e: any) => setPassword(e.target.value)}
                value={password}
              />
            </FlexBox>
            {formAlert && (
              <FlexBox style={{ margin: `${SPACING.space2} 0` }}>
                <CalloutBox type="error" fullWidth>
                  <Typography variant="bodySmall">
                    {formAlert.message}
                  </Typography>
                </CalloutBox>
              </FlexBox>
            )}
          </FlexBox>
          <Primary3DButton
            isLoading={isSubmittingData || isQuestionnaireLoading}
            disabled={isSubmittingData || isQuestionnaireLoading}
            type="submit"
            text="See my coaches"
            fullWidth
          />
        </form>
      </OnboardingPageContentWrapper>
    </OnboardingPageWrapper>
  )
}

export default CoachSignUpSkip
